/**
 * Re-export from shared lemmatizer.
 *
 * The canonical source is shared/utils/lemmatizer.ts.
 * This file is auto-synced by scripts/sync-shared.sh.
 * DO NOT EDIT — changes will be overwritten.
 *
 * @see shared/utils/lemmatizer.ts
 */

// --- AUTO-SYNCED CONTENT BELOW (from shared/utils/lemmatizer.ts) ---
// This file is kept as a copy because CRA cannot import outside src/.
// Run `scripts/sync-shared.sh` after editing shared/utils/lemmatizer.ts.

/**
 * Shared English Lemmatizer
 *
 * Converts inflected word forms to their base/dictionary form.
 * Handles: plurals, verb conjugations, comparatives/superlatives.
 *
 * CANONICAL SOURCE — used by both client and server.
 * - Client: re-exported via client/src/utils/lemmatizer.ts
 * - Server: imported via tsconfig path @shared/utils/lemmatizer
 */

// Irregular plurals mapping
const irregularPlurals: Record<string, string> = {
  children: 'child',
  people: 'person',
  men: 'man',
  women: 'woman',
  feet: 'foot',
  teeth: 'tooth',
  geese: 'goose',
  mice: 'mouse',
  lives: 'life',
  wives: 'wife',
  knives: 'knife',
  leaves: 'leaf',
  halves: 'half',
  selves: 'self',
  shelves: 'shelf',
  wolves: 'wolf',
  thieves: 'thief',
  loaves: 'loaf',
};

// Irregular present participles that don't follow standard -ing rules
const irregularPresentParticiples: Record<string, string> = {
  being: 'be',
  having: 'have',
  going: 'go',
  doing: 'do',
  coming: 'come',
  lying: 'lie',
  dying: 'die',
  tying: 'tie',
  making: 'make',
  taking: 'take',
  giving: 'give',
  writing: 'write',
  riding: 'ride',
  driving: 'drive',
  rising: 'rise',
  shining: 'shine',
  biting: 'bite',
  hiding: 'hide',
};

// Known past participles for accurate form detection
const pastParticiples = new Set([
  'been', 'done', 'gone', 'seen', 'taken', 'given', 'written', 'spoken',
  'broken', 'chosen', 'fallen', 'driven', 'ridden', 'eaten', 'drunk',
  'flown', 'grown', 'drawn', 'thrown', 'shown', 'worn', 'sung', 'swum',
  'begun', 'known', 'gotten', 'woken', 'lain', 'frozen', 'stolen', 'hidden',
  'bitten', 'risen', 'shaken', 'mistaken', 'forgotten', 'forgiven',
]);

// Irregular verbs mapping (all forms -> base)
const irregularVerbs: Record<string, string> = {
  // be verb - all forms
  am: 'be',
  is: 'be',
  are: 'be',
  was: 'be',
  were: 'be',
  been: 'be',
  // have verb
  has: 'have',
  had: 'have',
  // do verb
  does: 'do',
  did: 'do',
  done: 'do',
  went: 'go',
  gone: 'go',
  came: 'come',
  saw: 'see',
  seen: 'see',
  took: 'take',
  taken: 'take',
  made: 'make',
  got: 'get',
  gotten: 'get',
  said: 'say',
  knew: 'know',
  known: 'know',
  thought: 'think',
  found: 'find',
  gave: 'give',
  given: 'give',
  told: 'tell',
  felt: 'feel',
  became: 'become',
  left: 'leave',
  brought: 'bring',
  began: 'begin',
  begun: 'begin',
  kept: 'keep',
  held: 'hold',
  wrote: 'write',
  written: 'write',
  stood: 'stand',
  heard: 'hear',
  let: 'let',
  meant: 'mean',
  set: 'set',
  met: 'meet',
  ran: 'run',
  paid: 'pay',
  sat: 'sit',
  spoke: 'speak',
  spoken: 'speak',
  broke: 'break',
  broken: 'break',
  chose: 'choose',
  chosen: 'choose',
  fell: 'fall',
  fallen: 'fall',
  built: 'build',
  sent: 'send',
  spent: 'spend',
  lost: 'lose',
  caught: 'catch',
  taught: 'teach',
  bought: 'buy',
  led: 'lead',
  read: 'read',
  grew: 'grow',
  grown: 'grow',
  drew: 'draw',
  drawn: 'draw',
  threw: 'throw',
  thrown: 'throw',
  showed: 'show',
  shown: 'show',
  won: 'win',
  wore: 'wear',
  worn: 'wear',
  sang: 'sing',
  sung: 'sing',
  swam: 'swim',
  swum: 'swim',
  drove: 'drive',
  driven: 'drive',
  rode: 'ride',
  ridden: 'ride',
  ate: 'eat',
  eaten: 'eat',
  drank: 'drink',
  drunk: 'drink',
  flew: 'fly',
  flown: 'fly',
  lay: 'lie',
  lain: 'lie',
  slept: 'sleep',
  woke: 'wake',
  woken: 'wake',
};

/**
 * Get possible lemmas (base forms) for a word
 * Returns array of possible base forms, ordered by likelihood
 */
export function getLemmas(word: string): string[] {
  const lower = word.toLowerCase();
  const lemmas: string[] = [lower]; // Always include original

  // Check irregular forms first
  if (irregularPlurals[lower]) {
    lemmas.push(irregularPlurals[lower]);
  }
  if (irregularVerbs[lower]) {
    lemmas.push(irregularVerbs[lower]);
  }
  if (irregularPresentParticiples[lower]) {
    lemmas.push(irregularPresentParticiples[lower]);
  }

  // Handle regular patterns

  // Plurals ending in -ies -> -y (e.g., cities -> city)
  if (lower.endsWith('ies') && lower.length > 4) {
    lemmas.push(lower.slice(0, -3) + 'y');
  }

  // Plurals ending in -es (e.g., boxes -> box, churches -> church)
  if (lower.endsWith('es') && lower.length > 3) {
    lemmas.push(lower.slice(0, -2)); // boxes -> box
    lemmas.push(lower.slice(0, -1)); // churches -> churche (will be handled by -s rule)
  }

  // Plurals ending in -s (e.g., cats -> cat, buildings -> building)
  if (lower.endsWith('s') && lower.length > 2 && !lower.endsWith('ss')) {
    lemmas.push(lower.slice(0, -1));
  }

  // Verbs ending in -ing
  if (lower.endsWith('ing') && lower.length > 5) {
    const stem = lower.slice(0, -3);
    lemmas.push(stem); // running -> runn (might need more processing)
    lemmas.push(stem + 'e'); // making -> make
    // Handle doubled consonants: running -> run
    if (stem.length > 1 && stem[stem.length - 1] === stem[stem.length - 2]) {
      lemmas.push(stem.slice(0, -1));
    }
  }

  // Verbs ending in -ed
  if (lower.endsWith('ed') && lower.length > 3) {
    const stem = lower.slice(0, -2);
    lemmas.push(stem); // walked -> walk
    lemmas.push(lower.slice(0, -1)); // used -> use (d removed)
    // Handle -ied -> -y (e.g., studied -> study)
    if (lower.endsWith('ied')) {
      lemmas.push(lower.slice(0, -3) + 'y');
    }
    // Handle doubled consonants: stopped -> stop
    if (stem.length > 1 && stem[stem.length - 1] === stem[stem.length - 2]) {
      lemmas.push(stem.slice(0, -1));
    }
  }

  // Comparatives/superlatives: -er, -est
  if (lower.endsWith('er') && lower.length > 3) {
    lemmas.push(lower.slice(0, -2)); // bigger -> bigg
    lemmas.push(lower.slice(0, -1)); // larger -> large
    // Handle doubled consonants: bigger -> big
    const stem = lower.slice(0, -2);
    if (stem.length > 1 && stem[stem.length - 1] === stem[stem.length - 2]) {
      lemmas.push(stem.slice(0, -1));
    }
  }

  if (lower.endsWith('est') && lower.length > 4) {
    lemmas.push(lower.slice(0, -3)); // biggest -> bigg
    lemmas.push(lower.slice(0, -2)); // largest -> large
    // Handle doubled consonants: biggest -> big
    const stem = lower.slice(0, -3);
    if (stem.length > 1 && stem[stem.length - 1] === stem[stem.length - 2]) {
      lemmas.push(stem.slice(0, -1));
    }
  }

  // Third person singular: -s, -es
  // Already handled by plural rules above

  // Remove duplicates and return
  return Array.from(new Set(lemmas));
}

/**
 * Find a word in a CEFR level map, trying lemmatized forms
 * Returns the CEFR level if found, undefined otherwise
 */
export function findInCefrMap<T>(
  word: string,
  cefrMap: Record<string, T>
): T | undefined {
  const lemmas = getLemmas(word);

  for (const lemma of lemmas) {
    if (cefrMap[lemma]) {
      return cefrMap[lemma];
    }
  }

  return undefined;
}

/**
 * Check if two words share a common lemma (base form)
 * Returns the shared lemma if found, undefined otherwise
 */
export function getSharedLemma(word1: string, word2: string): string | undefined {
  if (!word1?.trim() || !word2?.trim()) {
    return undefined;
  }

  const lemmas1 = getLemmas(word1);
  const lemmaSet2 = new Set(getLemmas(word2));

  for (const lemma of lemmas1) {
    if (lemmaSet2.has(lemma)) {
      return lemma;
    }
  }

  return undefined;
}

/**
 * Identify the morphological relationship between a word and its lemma
 */
export function identifyWordForm(word: string, lemma: string): string {
  const lower = word.toLowerCase();
  const lowerLemma = lemma.toLowerCase();

  // Same word
  if (lower === lowerLemma) {
    return 'base';
  }

  // Check irregular plurals
  if (irregularPlurals[lower] === lowerLemma) {
    return 'irregular_plural';
  }

  // Check irregular present participles
  if (irregularPresentParticiples[lower] === lowerLemma) {
    return 'present_participle';
  }

  // Check irregular verbs - use pastParticiples set for accurate detection
  if (irregularVerbs[lower] === lowerLemma) {
    // Present tense forms
    if (lower === 'am' || lower === 'is' || lower === 'are') return 'present_be';
    if (lower === 'has' || lower === 'does') return 'third_person';
    // Past tense forms
    if (lower === 'was' || lower === 'were') return 'past_be';
    if (pastParticiples.has(lower)) return 'past_participle';
    return 'past_tense';
  }

  // Check regular patterns
  if (lower.endsWith('ing')) return 'present_participle';
  if (lower.endsWith('ed')) return 'past_tense';
  if (lower.endsWith('ies') || lower.endsWith('es') || (lower.endsWith('s') && !lower.endsWith('ss'))) {
    if (lowerLemma.match(/^(be|have|do|go|say|get|make|know|think|take)$/)) {
      return 'third_person';
    }
    return 'plural_or_third_person';
  }
  if (lower.endsWith('er')) return 'comparative';
  if (lower.endsWith('est')) return 'superlative';

  return 'inflected';
}

// Type definitions for lemma relation info
export interface LemmaRelationInfo {
  userWord: string;
  correctWord: string;
  lemma: string;
  userForm: string;
  correctForm: string;
  translationKey: string;
}

/**
 * Resolve ambiguous 'plural_or_third_person' using dependency graph role
 * Verb-related roles -> 'third_person', otherwise -> 'plural'
 */
export function resolveAmbiguousForm(form: string, wordRole?: string): string {
  if (form !== 'plural_or_third_person') return form;
  if (!wordRole) return 'plural';
  const verbRoles = ['Verb', 'Auxiliary', 'Modal'];
  return verbRoles.includes(wordRole) ? 'third_person' : 'plural';
}

/**
 * Known form values that have i18n translations.
 * Maps AI-hallucinated variants to standard keys.
 */
const FORM_ALIASES: Record<string, string> = {
  past_singular: 'past_tense',
  past_plural: 'past_tense',
  past: 'past_tense',
  present: 'base',
  present_singular: 'third_person',
  present_plural: 'base',
  gerund: 'present_participle',
  infinitive: 'base',
  participle: 'past_participle',
  singular: 'base',
};

const KNOWN_FORMS = new Set([
  'base', 'past_tense', 'past_participle', 'present_participle',
  'third_person', 'plural', 'comparative', 'superlative',
  'present_be', 'past_be', 'plural_or_third_person',
  'irregular_plural', 'inflected',
]);

/**
 * Normalize a form value from the dependency graph to a known i18n key.
 * Returns the computed fallback if the value is unrecognized.
 */
export function normalizeFormValue(form: string | undefined, fallback: string): string {
  if (!form) return fallback;
  if (KNOWN_FORMS.has(form)) return form;
  if (FORM_ALIASES[form]) return FORM_ALIASES[form];
  return fallback;
}

/**
 * Get lemma relation information for i18n translation
 * Returns info object with translation key and parameters
 */
export function getLemmaRelationInfo(
  userWord: string,
  correctWord: string,
  sharedLemma: string,
  correctWordRole?: string,
  correctWordForm?: string,
): LemmaRelationInfo {
  const rawUserForm = identifyWordForm(userWord, sharedLemma);
  const rawCorrectForm = identifyWordForm(correctWord, sharedLemma);
  const computedCorrectForm = resolveAmbiguousForm(rawCorrectForm, correctWordRole);
  const correctForm = correctWordForm
    ? normalizeFormValue(correctWordForm, computedCorrectForm)
    : computedCorrectForm;
  const userForm = resolveAmbiguousForm(rawUserForm, correctWordRole);
  const userLower = userWord.toLowerCase();
  const correctLower = correctWord.toLowerCase();
  const lemmaLower = sharedLemma.toLowerCase();

  let translationKey = 'generic';

  if (userForm === 'present_be' && correctForm === 'past_be') {
    translationKey = 'presentToPast';
  } else if (userForm === 'past_be' && correctForm === 'present_be') {
    translationKey = 'pastToPresent';
  } else if ((userForm === 'past_tense' || userForm === 'past_participle' || userForm === 'past_be') && correctForm === 'base') {
    translationKey = 'pastToBase';
  } else if (userForm === 'base' && (correctForm === 'past_tense' || correctForm === 'past_participle' || correctForm === 'past_be')) {
    translationKey = 'baseToPast';
  } else if (userForm === 'present_participle' && correctForm !== 'present_participle') {
    translationKey = 'ingToOther';
  } else if (correctForm === 'present_participle' && userForm !== 'present_participle') {
    translationKey = 'otherToIng';
  } else if ((userForm === 'plural' || userForm === 'irregular_plural') && correctForm === 'base') {
    translationKey = 'pluralToSingular';
  } else if (userForm === 'third_person' && correctForm === 'base') {
    translationKey = 'thirdPersonToBase';
  } else if ((correctForm === 'plural' || correctForm === 'irregular_plural') && userForm === 'base') {
    translationKey = 'singularToPlural';
  } else if (correctForm === 'third_person' && userForm === 'base') {
    translationKey = 'baseToThirdPerson';
  } else if (userForm === 'comparative' || userForm === 'superlative') {
    translationKey = 'comparativeToOther';
  }

  return {
    userWord: userLower,
    correctWord: correctLower,
    lemma: lemmaLower,
    userForm,
    correctForm,
    translationKey,
  };
}
